<template>
  <div class="organizational-skills-tree">
    <div class="w-full h-screen bg-gray-100 flex overflow-x-auto" style="padding-top:40px">
      <!-- Left Sidebar - Hierarchy Navigation & Skills Legend Tabs -->
      <div :class="['jobLeft sidebar bg-white border-r border-gray-200 flex flex-col flex-shrink-0', { 'collapsed': isSidebarCollapsed }]">
        <!-- Floating Toggle Button -->
        <button
          @click="isSidebarCollapsed = !isSidebarCollapsed"
          class="sidebar-toggle-btn-floating"
          :title="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        >
          <v-icon size="16">mdi-chevron-right</v-icon>
        </button>
        <!-- Tabs -->
        <div v-if="!isSidebarCollapsed" class="sidebar-tabs flex border-b border-gray-200">
          <button
            :class="['sidebar-tab', sidebarTab === 'skills' ? 'active' : '']"
            @click="sidebarTab = 'skills'"
          >
          <v-icon>mdi-head-cog</v-icon>
            Skills
          </button>          
          <button
            :class="['sidebar-tab', sidebarTab === 'hierarchy' ? 'active' : '']"
            @click="sidebarTab = 'hierarchy'"
          >
          <v-icon>mdi-file-tree-outline</v-icon>
          Hierarchy</button>

        </div>
        <!-- Hierarchy Tree View -->
        <div v-if="!isSidebarCollapsed && sidebarTab === 'hierarchy'" :class="['flex-1 flex flex-col sidebar-container', { 'hierarchy-padding': sidebarTab === 'hierarchy' }]" style="max-height:calc(100vh - 320px)">
          <div class="flex-1 overflow-y-auto">
            <SkillHierarchySearch
              :items="passedParamsDocuments"
              :preSelected="preSelectedHierarchyFilters"
              :preSelectedClassifiers="preSelectedClassifierFilters"
              :hierarchyTypes="hierarchyTypes"
              :classifierTypes="classifierTypes"
              @filterChanged="doHierarchyFilter"
              @exploreDocuments="handleExploreDocuments"
              :framework="framework"
            ></SkillHierarchySearch>
          </div>
        </div>
        <!-- Skills Legend View -->
        <div v-else-if="!isSidebarCollapsed && sidebarTab === 'skills'" :class="['flex-1 flex flex-col sidebar-container', { 'hierarchy-padding': sidebarTab === 'hierarchy' }]"  style="max-height:calc(100vh - 320px); overflow:hidden; width:100%">
          <div class="p-4 border-b border-gray-200 flex-shrink-0" style="padding-bottom:0px !important; padding-left:0px !important;">
            <div style="display: flex; gap: 15px; align-items: center; padding-bottom:16px">
              <div class="sub-header-style-1">Skills in your selected jobs:</div>
            </div>
            
            <!-- Add New Skills Button -->
            <div :class="['filters-button-container', { 'expanded': showFilters }]" style="display: flex; justify-content: space-between;">
              <v-btn depressed small @click="showAddSkillsDialog = true">
                <v-icon left small>mdi-plus</v-icon>
                Add a Skill to List
              </v-btn>
              <v-btn 
                class="ml-4" 
                :class="{ 'filters-expanded': showFilters }" 
                depressed 
                small 
                @click="toggleFilters"
              >
                Filters
                <v-icon right small :class="{ 'rotate-180': showFilters }">mdi-chevron-up</v-icon>
              </v-btn>              
            </div>
            
            <!-- Global Filter Controls -->
            <div v-if="showFilters" class="filters-section space-y-3">
              <!-- Skill Name Search -->
              <div class="filter-group">
                <label class="filter-label text-xs font-medium text-gray-700 mb-1">
                  Skill
                </label>
                <v-text-field
                  v-model="globalFilters.nameSearch"
                  placeholder="Type to search skills..."
                  dense
                  outlined
                  hide-details
                  clearable
                  class="skill-search-field"
                  prepend-inner-icon="mdi-magnify"
                ></v-text-field>
              </div>
              
              <!-- Skill Types Filter -->
              <div class="filter-group">
                <label class="filter-label text-xs font-medium text-gray-700 mb-1">
                  Skill Types
                </label>
                <v-select
                  v-model="globalFilters.selectedTypes"
                  :items="availableSkillTypes"
                  multiple
                  chips
                  small-chips
                  dense
                  outlined
                  hide-details                  
                  :menu-props="{ 
                    closeOnContentClick: false,
                    maxHeight: 300,
                    transition: 'slide-y-transition'
                  }"
                  :placeholder="skillTypesLabel"
                  class="skill-types-filter"
                >
                  <template v-slot:selection="{ index }">
                    <span v-if="index === 0" class="text-sm">{{ skillTypesLabel }}</span>
                  </template>
                  <template v-slot:prepend-item>
                    <v-list-item @click="toggleAllTypes">
                      <v-list-item-action>
                        <v-icon :color="globalFilters.selectedTypes.length > 0 ? 'indigo darken-4' : ''">
                          {{ allTypesSelected ? 'mdi-close-box' : 'mdi-checkbox-marked' }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider></v-divider>
                  </template>
                </v-select>
              </div>
              
              <!-- Proficiency Levels Filter -->
              <div class="filter-group">
                <label class="filter-label text-xs font-medium text-gray-700 mb-1">
                  Proficiency Levels
                </label>
                <v-select
                  v-model="globalFilters.selectedProficiencyLevels"
                  :items="availableProficiencyLevels"
                  multiple
                  chips
                  small-chips
                  dense
                  outlined
                  hide-details
                  :placeholder="proficiencyLevelsLabel"
                  class="proficiency-levels-filter"
                >
                  <template v-slot:selection="{ index }">
                    <span v-if="index === 0" class="text-sm">{{ proficiencyLevelsLabel }}</span>
                  </template>
                  <template v-slot:prepend-item>
                    <v-list-item @click="toggleAllProficiencyLevels">
                      <v-list-item-action>
                        <v-icon :color="globalFilters.selectedProficiencyLevels.length > 0 ? 'indigo darken-4' : ''">
                          {{ allProficiencyLevelsSelected ? 'mdi-close-box' : 'mdi-checkbox-marked' }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider></v-divider>
                  </template>
                </v-select>
              </div>
              
              <!-- Filter Summary -->
              <div class="filter-summary text-xs text-gray-600">
                <button 
                  v-if="hasActiveFilters"
                  @click="clearAllFilters"
                  class="ml-2 text-blue-600 hover:text-blue-800 underline"
                >
                  Clear filters
                </button>
              </div>
            </div>
          </div>
          
          <div class="flex-1" style="overflow:hidden; padding-top:10px">
            <div :class="['legend-list', showFilters ? 'filters-shown' : 'filters-hidden']">
              <!-- Legend Header -->
              <div class="legend-header">
                <span>Skill Name</span>
                <span>Count</span>
              </div>
              
              <div 
                v-for="skill in filteredSkillsForLegend" 
                :key="'legend-' + skill.name" 
                class="legend-item-container"
                :class="{
                  focused: skill.isFocused,
                  unfocused: focusedSkills.length > 0 && !skill.isFocused,
                  'non-matching': !skill.isMatching
                }"
              >
                <div class="legend-item" style="cursor:pointer">
                  <div class="legend-expand">
                    <v-icon 
                      size="16" 
                      class="transition-transform"
                      @click="toggleSkillDescriptor(skill.name)"
                    >
                      {{ expandedDescriptors.includes(skill.name) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                    </v-icon>
                  </div>              
                  <div v-if="viewMode === 'bumpChart'" class="legend-color" :style="{ background: skill.color }"></div>
                  <span class="legend-text"
                  @click="onLegendSkillClick(skill.name)">{{ skill.name }}</span>
                  <span class="skill-count">({{ skill.documentCount }})</span>
                  <div class="legend-indicator" v-if="skill.isFocused">●</div>
                </div>

                <!-- Accordion Content -->
                <div 
                  v-if="expandedDescriptors.includes(skill.name)"
                  class="accordion-content border-t border-gray-100 p-3 bg-gray-50"
                >
                  <div>
                      <p class="text-xs text-gray-700 pl-2 border-l-2 border-gray-200">
                        {{ skill.descriptor }}
                      </p>
                  </div>
                  <div v-if="skill.ratings && skill.ratings.length > 0" class="space-y-2">
                    <div 
                      v-for="rating in skill.ratings" 
                      :key="rating.id"
                      class="flex flex-col"
                    >
                      <div class="flex items-center gap-2 mb-1">
                        <span 
                          :class="['px-2 py-1 rounded text-xs font-medium', getRatingColorFromName(rating.name)]"
                        >
                          {{ rating.name }}
                        </span>
                      </div>
                      <p class="text-xs text-gray-700 pl-2 border-l-2 border-gray-200">
                        {{ rating.description || 'No description available' }}
                      </p>
                    </div>
                  </div>
                  <div v-else class="text-xs text-gray-500 italic">
                    No proficiency descriptors available for this skill.
                  </div>
                </div>                 
              </div>
            </div>
          </div>
          <div class="p-3 border-t flex-shrink-0">
            <button
              v-if="focusedSkills.length > 0"
              @click="handleSkillFocus([])"
              class="text-xs text-blue-600 underline"
            >
              Show All Skills
            </button>
          </div>
        </div>
      </div>
      <!-- Right Main Content -->
      <div class="jobRight">
        <!-- Header -->
        <div class="rightContentHeader">
          <h1 class="text-2xl font-bold text-gray-900">Compare Skills in  {{ totalDocumentCount }} Job{{ totalDocumentCount === 1 ? '' : 's' }}</h1>
        </div>
        <div class="actionsContainer">
          <div class="flex items-center justify-between" style="display: flex;align-items: baseline;">
            <div class="flex items-left" style="align-items:center; gap: 8px;">
              <!-- Refresh Data Button -->
              <v-btn
                @click="refreshAllData"
                small
                depressed
                class="refresh-btn"
                title="Refresh all data from server"
                :loading="isRefreshing"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Refresh
              </v-btn>

              <!-- Save Changes Button -->
              <v-btn
                :disabled="!hasPendingChanges"
                @click="saveAllChanges"
                depressed
                small
                class="save-btn"
                :title="`Save ${pendingChangesCount} pending change${pendingChangesCount !== 1 ? 's' : ''}`"
              >
                <v-icon left small>mdi-check</v-icon>
                Save Changes ({{ pendingChangesCount }})
              </v-btn>
              
              <!-- Global Revert Changes Button -->
              <v-btn
                :disabled="!hasPendingChanges"
                @click="revertAllChanges"
                depressed
                small
                class="revert-btn"
                :title="`Revert ${pendingChangesCount} pending change${pendingChangesCount !== 1 ? 's' : ''}`"
              >
                <v-icon left small>mdi-trash-can-outline</v-icon>
                Discard Changes ({{ pendingChangesCount }})
              </v-btn>              
            </div>
            
            <div class="view-controls">
              <button
                title="Document View"
                @click="viewMode = 'details'"
                :class="['view-control', { 'active': viewMode === 'details' }]"
              >
                <v-icon size="16" :color="viewMode === 'details' ? '#000' : '#666'">mdi-file-document-multiple-outline</v-icon>
                Document View
              </button>
              <button
                title="Chart View"
                @click="viewMode = 'bumpChart'"
                :class="['view-control', { 'active': viewMode === 'bumpChart' }]"
                
              >
                <v-icon size="16" :color="viewMode === 'bumpChart' ? '#000' : '#666'">mdi-chart-bell-curve</v-icon>
                Chart View
              </button>
              <button
                title="Matrix View"
                @click="viewMode = 'matrix'"
                :class="['view-control', { 'active': viewMode === 'matrix' }]"
              >
                <v-icon size="16" :color="viewMode === 'matrix' ? '#000' : '#666'">mdi-grid</v-icon>
                Matrix View
              </button>
            </div>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="main-content-area" :class="{ 'document-view': viewMode === 'details' }">
          <!-- Loading state - check first and use skillJobViewState.loading from cSkillWorkspace -->
          <div v-if="skillJobViewState.loading" class="loading-state">
            <div class="loading-content">
              <v-progress-circular
                :size="50"
                color="primary"
                indeterminate
              ></v-progress-circular>
              <h3 class="mt-4">Loading skills data...</h3>
            </div>
          </div>

          <!-- Empty state - only when not loading and we have an empty state message -->
          <div v-else-if="skillJobViewState.emptyStateMessage" class="empty-state">
            <div class="empty-content">
              <v-icon size="64" color="grey lighten-1">mdi-briefcase-search</v-icon>
              <h2>{{ skillJobViewState.emptyStateMessage }}</h2>
            </div>
          </div>

          <!-- No results state - when not loading, no empty state message, but no filtered items -->
          <div v-else-if="!skillJobViewState.loading && !skillJobViewState.emptyStateMessage && filteredItems.length === 0" class="empty-state">
            <div class="empty-content">
              <v-icon size="64" color="grey lighten-1">mdi-file-search-outline</v-icon>
              <h2>No documents found</h2>
              <p class="text-gray-600 mt-2">No documents match your current selection criteria. Try adjusting your hierarchy filters or document type selections.</p>
            </div>
          </div>

          <!-- Main content with skills data -->
          <div v-else class="skills-content">
            <!-- Bump Chart View - Isolated Container -->
            <div v-if="viewMode === 'bumpChart' && filteredItems.length > 1" class="chart-view-container">
              <SkillBumpChart
                :items="filteredItems"
                :focusedSkills="focusedSkills"
                :onSkillFocus="handleSkillFocus"
                :tags="tags"
                :globalFilters="globalFilters"
                :manuallyAddedSkills="manuallyAddedSkills"
                :clientProficiencyLevels="availableProficiencyLevels"
                @update-document-data="handleDocumentDataUpdate"
                @update:items="handleItemsUpdate"
              />
            </div>
            
            <!-- Matrix View - Isolated Container -->
            <div v-if="viewMode === 'matrix' && filteredItems.length > 1" class="matrix-view-container">
              <SkillBumpChart
                :items="filteredItems"
                :focusedSkills="focusedSkills"
                :onSkillFocus="handleSkillFocus"
                :tags="tags"
                :globalFilters="globalFilters"
                :manuallyAddedSkills="manuallyAddedSkills"
                :initialDisplayMode="'matrix'"
                :clientProficiencyLevels="availableProficiencyLevels"
                @update-document-data="handleDocumentDataUpdate"
                @update:items="handleItemsUpdate"
              />
            </div>
            
            <!-- Document View and Other Views -->
            <div v-else class="document-view-container" style="padding: 0px 16px 5px 0px">
              <!-- Bump chart requires multiple items -->
              <div v-if="viewMode === 'bumpChart' && filteredItems.length <= 1" class="text-center py-12 text-gray-500">
                <div class="text-6xl mb-4">📊</div>
                <p>Select multiple items to view the bump chart comparison</p>
              </div>
              
              <!-- Item cards -->
              <div v-else class="flex min-w-max" style="gap: 8px">
              <div
                v-for="(item, index) in filteredItems"
                :key="item.id"
                :class=" [
                  'flex-shrink-0 bg-white border rounded-lg shadow-sm transition-all duration-300 item-card',
                  { 'drop-zone-active': draggedItemIndex !== null && draggedItemIndex !== index && dropIndicatorIndex === index }
                ]"
                :style="{ width: getCardWidth(item) }"
                :draggable="true"
                @dragstart="handleItemDragStart($event, item, index)"
                @dragover="handleItemDragOver($event, index)"
                @dragenter="handleItemDragEnter($event, index)"
                @dragleave="handleItemDragLeave($event, index)"
                @drop="handleItemDrop($event, index)"
                @dragend="handleItemDragEnd($event)"
              >
                <!-- Drop indicator line -->
                <div 
                  v-if="draggedItemIndex !== null && draggedItemIndex !== index && dropIndicatorIndex === index"
                  class="drop-indicator"
                ></div>
                
                <!-- Card Header -->
                <div class="document-card-header">
                  <div class="flex" style="align-items: baseline; max-width: 90%; text-overflow: ellipsis; white-space: nowrap; word-break: break-word;">
                    <!-- Item drag handle -->
                    <div class="item-drag-handle mr-1 text-gray-400 hover:text-gray-600 flex-shrink-0" title="Drag to reorder items">
                      <v-icon size="16">mdi-drag</v-icon>
                    </div>
                    
                    <div style="width:100%" @click="toggleExpanded(item.id)">
                      <span v-if="item.isDocument" class="text-orange-600">📄</span>
                      
                      <div class="flex-1 min-w-0" :title="(item.isDocument ? item.path.slice(0, -2).join(' > ') + ` > ${item.parentJobTitle}` : item.path.slice(0, -1).join(' > ')) + ' > ' + item.name">
                        <h3 class="font-semibold text-gray-900 text-sm leading-tight truncate" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                          {{ item.isDocument ? item.path.slice(0, -2).join(' > ') + ` > ${item.parentJobTitle}` : item.path.slice(0, -1).join(' > ') }}{{ ' > ' + item.name }} 
                        </h3>
                        <div class="text-xs text-gray-500 mt-1 truncate" style="max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">

                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Card Content -->
                <div style="min-height:calc(100vh - 420px); max-height:calc(100vh - 420px);overflow:hidden hidden;">
                  <div class="space-y-3">
                    <DocumentDetails
                      v-if="viewMode === 'details'"
                      :item="item"
                      :focusedSkills="focusedSkills"
                      :globalFilters="globalFilters"
                      :hasDocumentPendingChangesByKey="hasDocumentPendingChangesByKey"
                      :tags="tags"
                      :availableProficiencyLevels="availableProficiencyLevels"
                      @document-skill-update="handleDocumentSkillUpdate"
                      @viewDoc="(event, id) => viewNodeDoc(event, id)"
                      :rerenderKeyVal="rerenderKey"
                      @toggleSkillFocus="handleToggleSkillFocus"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <Notification :notification="notification" />

    <!-- Saving overlay -->
    <div v-if="isSaving" class="saving-overlay">
      <div class="saving-message">
        <v-icon size="48" color="primary">mdi-loading</v-icon>
        <p>Saving changes, please wait...</p>
      </div>
    </div>
    <DocSummary
      :doc_id="documentSummary.doc_id"
      :trigger="documentSummary.trigger"
      @openDocument="openDocFromPreview"
    ></DocSummary>
    
    <!-- Add Skills Dialog -->
    <v-dialog v-model="showAddSkillsDialog" max-width="1200px" scrollable>
      <v-card style="overflow:hidden">
        <v-card-title style="display: table-row !important;">
          <v-row dense>
            <v-col cols="2">
              <b>Add skills</b>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="pa-3 ml-2">
        <AddSkills
          v-if="showAddSkillsDialog"
          :show="showAddSkillsDialog"
          :tag_type_id="tagTypeIdForAddSkills"
          :existing_tags="allSkillsWithDescriptors"
          :hide_proficiency="true"
          @close="showAddSkillsDialog = false"
          @save="handleAddSkillsSave"
        />
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</div>
</template>

<script>
import { ref, computed, inject, nextTick, watch, onMounted } from 'vue'
import DocumentDetails from './cDocumentDetails.vue'
import SkillBumpChart from './cSkillBumpChart.vue'
import Notification from "@/components/common/SnackBar.vue";
import DocSummary from "@/components/common/DocViewPopUp";
import SkillHierarchySearch from "./cSkillHierarchySearch.vue";
import { mapState } from "vuex";
import axios from 'axios';
import AddSkills from './cAddSkills.vue'
import { usePageContext } from "@/composables/agent/atlasFlyout";

export default {
  name: 'OrganizationalSkillsTree',
  components: {
    DocumentDetails,
    SkillBumpChart,
    Notification, DocSummary, AddSkills, SkillHierarchySearch
  },
  props: {
    organizationalData: {
      type: Array,
      required: true
    },
    tags: {
      type: Array,
      required: true
    },
    framework: {
      type: Object
    },
    skillJobViewState: {
      type: Object,
      default: () => ({
        loading: false,
        emptyStateMessage: null,
        passedParams: []
      })
    }
  },
  emits: ['refresh-data','openDocument','exploreSkills'],
  computed: {
    ...mapState({
      hierarchyTypes: (state) => state.docs.hierarchyTypes,
      classifierTypes: (state) => state.docs.classifierTypes,
      cleanItems: (state) => state.docs.docsList
    })
  },
  setup(props, { emit }) {
    const store = inject('store')
    const notification = ref({
      text: "",
      type: "success"
    })
    const selectedItems = ref([])
    const viewMode = ref('details')
    const expandedItems = ref([])
    const sidebarTab = ref('skills') // Will be set based on document availability
    const isSidebarCollapsed = ref(false)
    const hasInitializedSidebarTab = ref(false) // Track if we've set the initial sidebar tab
    const showFilters = ref(false)
    const focusedSkills = ref([])
    const draggedItem = ref(null)
    const draggedItemIndex = ref(null)
    const itemDisplayOrder = ref([]) // Track custom display order
    const dropIndicatorIndex = ref(null) // Track where drop indicator should show
    const expandedDescriptors = ref([]) // Track which skill descriptors are expanded
    const isSaving = ref(false); // Track saving state
    const documentSummary = ref({
      trigger: null,
      doc_id: null,
    })
    // Centralized pending changes tracking - track document state, not individual changes
    const pendingChanges = ref(new Map()) // Map<documentKey, documentState>
    const originalDocumentData = ref(new Map()) // Map<documentKey, originalTags>
    const rerenderKey = ref(0)
    // Global filters for skills - initialize with default values to prevent reactive loops
    const globalFilters = ref({
      nameSearch: '',
      selectedTypes: [],
      selectedProficiencyLevels: []
    })

    // Add refresh state
    const isRefreshing = ref(false)
    const minDocumentCount = ref(1)

    // Optimized focused skills handling with immediate UI updates
    const handleSkillFocus = (skills) => {
      // Update focused skills immediately for instant UI feedback
      focusedSkills.value = [...skills]
      
      // Force immediate DOM update for visual feedback
      nextTick(() => {
        // Additional processing can happen here if needed
      })
    }

    const triggerNotification = (msg, type) => {
      notification.value = {
        text: msg,
        type: type
      };
    }

    const openDocFromPreview = (event) => {
      emit('openDocument', event);
    }

    const viewNodeDoc = (event, item) => {
      documentSummary.value.doc_id = item;
      documentSummary.value.trigger = event;
    }

    // Optimized legend click with immediate visual feedback
    const onLegendSkillClick = (skillName) => {
      const currentSkills = [...focusedSkills.value]
      const idx = currentSkills.indexOf(skillName)
      
      let newSkills
      if (idx > -1) {
        newSkills = currentSkills.filter(s => s !== skillName)
      } else {
        newSkills = [...currentSkills, skillName]
      }
      
      // Update immediately for instant visual feedback
      focusedSkills.value = newSkills
      
      // Synchronize with child components
      nextTick(() => {
        // Any additional processing
      })
    }

    // Build hierarchical structure for tree navigation including documents
    const buildTreeStructure = (data, level = 0, path = []) => {
      return data.map(item => {
        const currentPath = [...path, item.name]
        const itemWithMeta = {
          ...item,
          path: currentPath,
          level,
          id: currentPath.join('|'),
          hasChildren: (item.children && item.children.length > 0) || (item.documents && item.documents.length > 0)
        }

        // Add children (subdepartments/job titles)
        if (item.children && item.children.length > 0) {
          itemWithMeta.children = buildTreeStructure(item.children, level + 1, currentPath)
        } else if (item.documents && item.documents.length > 0) {
          // Add documents as children for job titles
          itemWithMeta.children = item.documents.map((doc, index) => ({
            ...doc,
            name: doc.jobFamily,
            path: [...currentPath, doc.jobFamily],
            level: level + 1,
            id: [...currentPath, doc.jobFamily].join('|'),
            hasChildren: false,
            isDocument: true,
            parentJobTitle: item.name,
            documentIndex: index
          }))
        }

        return itemWithMeta
      })
    }

    const treeData = computed(() => buildTreeStructure(props.organizationalData))

    // Get all job titles and documents for the main content
    const getAllJobTitlesAndDocuments = (data) => {
      const items = []
      
      const traverse = (nodes) => {
        nodes.forEach(node => {
          if (node.isDocument) {
            items.push(node)
          } else if (!node.hasChildren || (node.children && node.children.every(child => child.isDocument))) {
            items.push(node)
          } else if (node.children) {
            traverse(node.children)
          }
        })
      }
      
      traverse(data)
      return items
    }

    const allItems = computed(() => getAllJobTitlesAndDocuments(treeData.value))

    // Count total documents across all filtered items
    const totalDocumentCount = computed(() => {
      return filteredItems.value.reduce((count, item) => {
        return count + (item.documents ? item.documents.length : 0)
      }, 0)
    })

    // Get filtered items based on multiple selections with custom ordering
    const filteredItems = computed(() => {
      let baseItems
      if (selectedItems.value.length === 0) {
        baseItems = allItems.value.filter(item => !item.isDocument)
      } else {
        // Get all selected items from the tree
        const selectedNodes = []
        const findSelectedNodes = (nodes) => {
          nodes.forEach(node => {
            if (selectedItems.value.includes(node.id)) {
              selectedNodes.push(node)
            }
            if (node.children) {
              findSelectedNodes(node.children)
            }
          })
        }
        findSelectedNodes(treeData.value)
        
        // Filter items based on any selected path
        baseItems = allItems.value.filter(item => {
          return selectedNodes.some(selectedNode => {
            if (selectedNode.isDocument) {
              return item.id === selectedNode.id
            } else {
              return selectedNode.path.every((segment, index) => item.path[index] === segment)
            }
          })
        })
      }

      // Apply custom ordering if it exists
      if (itemDisplayOrder.value.length > 0) {
        const orderedItems = []
        const remainingItems = [...baseItems]
        
        // Add items in the specified order
        itemDisplayOrder.value.forEach(orderedId => {
          const itemIndex = remainingItems.findIndex(item => item.id === orderedId)
          if (itemIndex > -1) {
            orderedItems.push(remainingItems.splice(itemIndex, 1)[0])
          }
        })
        
        // Add any remaining items that weren't in the custom order
        orderedItems.push(...remainingItems)
        
        return orderedItems
      }
      
      return baseItems
    })

    /**
     * Atlas page context — declares the user's current skills view to
     * the global flyout so questions like "compare these roles' skills"
     * or "which of these are at Expert?" can be answered without the
     * user having to re-list the documents in their prompt.
     *
     * Mirrors the shape rm-portal-beta's SkillJobView uses
     * (`kind: 'skills'`) so the agent's `page_context` handler treats
     * both portals identically. Visible documents are walked off
     * `filteredItems` (top-level docs + nested `documents[]`),
     * de-duped, and capped at 60 entries to keep the prompt's token
     * budget sane on large filter matches. Cap-aware: when
     * `visible_document_truncated` is true the agent knows the list
     * is indicative not exhaustive.
     *
     * Cleared automatically by `usePageContext`'s `onBeforeUnmount`
     * when the user leaves the skills surface.
     */
    usePageContext(() => {
      const visibleDocs = []
      const seen = new Set()
      const pushDoc = (ref, name) => {
        const r = typeof ref === 'string' ? ref.trim() : ''
        if (!r || seen.has(r)) return
        seen.add(r)
        visibleDocs.push({
          reference: r,
          name: typeof name === 'string' && name ? name : null,
        })
      }
      for (const item of filteredItems.value || []) {
        if (item?.isDocument) {
          pushDoc(item.reference, item.jobFamily ?? item.name)
        }
        const docs = item?.documents
        if (Array.isArray(docs)) {
          for (const d of docs) {
            pushDoc(d?.reference, d?.jobFamily ?? d?.name)
          }
        }
      }

      const DOC_CAP = 60
      const docsPreview = visibleDocs.slice(0, DOC_CAP)
      const total = visibleDocs.length

      const filters = globalFilters.value || {}
      const summaryParts = ['Skills workspace']
      if (total > 0) {
        summaryParts.push(`${total} document${total === 1 ? '' : 's'} in view`)
      }
      if (filters.nameSearch) {
        summaryParts.push(`search: "${filters.nameSearch}"`)
      }
      if (summaryParts.length === 1) summaryParts.push('no filters applied')

      return {
        kind: 'skills',
        summary: summaryParts.join(' — '),
        data: {
          visible_documents: docsPreview,
          visible_document_count: total,
          visible_document_truncated: total > docsPreview.length,
          name_search: filters.nameSearch || null,
          selected_types: filters.selectedTypes || [],
          selected_proficiency_levels: filters.selectedProficiencyLevels || [],
        },
      }
    })

    // Handler for saving new skills (populate manuallyAddedSkills, merge with previous)
    const handleAddSkillsSave = (selectedSkills) => {
      // Avoid duplicates by value
      const existingValues = new Set(manuallyAddedSkills.value.map(s => s.value))
      const newSkills = selectedSkills.filter(skill => !existingValues.has(skill.value))
      manuallyAddedSkills.value = [
        ...manuallyAddedSkills.value,
        ...newSkills.map(skill => ({
          ...skill,
          isManual: true
        }))
      ]
      // Automatically add new manual skills to focusedSkills
      const newSkillNames = newSkills.map(skill => skill.value)
      focusedSkills.value = Array.from(new Set([...focusedSkills.value, ...newSkillNames]))
      showAddSkillsDialog.value = false
      // Optionally, trigger a refresh or notification.
    }

    // Enhanced skills collection to handle deleted items and manually added skills
    const allSkillsWithDescriptors = computed(() => {
      const itemsKey = filteredItems.value.map(item => item.id).join(',')

      const skillMap = new Map()
      let colorIndex = 0
      const colors = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6366F1']

      // Process skills from filteredItems (documents)
      filteredItems.value.forEach(item => {
        if (item.documents) {
          item.documents.forEach(doc => {
            (doc.tags || []).forEach(tag => {
              // Skip deleted skills in legend
              if (tag.isDeleted) return

              // Always get tagData from tags prop for ratings/descriptor
              const tagData = props.tags.find(t => t.tag_value_id === tag.tag_value_id || t.value === tag.value)

              if (!skillMap.has(tag.value)) {
                const classifierTypes = tag.classifiers 
                  ? tag.classifiers
                      .filter(classifier => classifier.value)
                      .map(classifier => classifier.value)
                  : []

                skillMap.set(tag.value, {
                  name: tag.value,
                  descriptor: tagData ? tagData.description : '',
                  color: colors[colorIndex % colors.length],
                  documentCount: 0,
                  ratings: tagData ? tagData.ratings : [],
                  classifierTypes: classifierTypes,
                  proficiencyLevels: new Set(),
                  tag_value_id: (tag.isNewSkill || String(tag.tag_value_id).startsWith('new_')) 
                    ? (tagData ? tagData.tag_value_id : null)
                    : tag.tag_value_id,
                  _cacheKey: itemsKey
                })
                colorIndex++
              }

              const skill = skillMap.get(tag.value)
              skill.documentCount++

              if (tag.name) {
                skill.proficiencyLevels.add(tag.name)
              }
            })
          })
        } else if (item.tags) {
          (item.tags || []).forEach(tag => {
            if (tag.isDeleted) return

            const tagData = props.tags.find(t => t.tag_value_id === tag.tag_value_id || t.value === tag.value)

            if (!skillMap.has(tag.value)) {
              const classifierTypes = tag.classifiers 
                ? tag.classifiers
                    .filter(classifier => classifier.value)
                    .map(classifier => classifier.value)
                : []

              skillMap.set(tag.value, {
                name: tag.value,
                descriptor: tagData ? tagData.description : '',
                color: colors[colorIndex % colors.length],
                documentCount: 0,
                ratings: tagData ? tagData.ratings : [],
                classifierTypes: classifierTypes,
                proficiencyLevels: new Set(),
                tag_value_id: (tag.isNewSkill || String(tag.tag_value_id).startsWith('new_')) 
                  ? (tagData ? tagData.tag_value_id : null)
                  : tag.tag_value_id,
                _cacheKey: itemsKey
              })
              colorIndex++
            }

            const skill = skillMap.get(tag.value)
            skill.documentCount++

            if (tag.name) {
              skill.proficiencyLevels.add(tag.name)
            }
          })
        }
      })

      // Process manually added skills (if any)
      manuallyAddedSkills.value.forEach(skill => {
        // Always get tagData from tags prop for ratings/descriptor
        const tagData = props.tags.find(t => t.value === skill.value)
        if (!skillMap.has(skill.value)) {
          skillMap.set(skill.value, {
            name: skill.value,
            descriptor: tagData ? tagData.description : (skill.description || ''),
            color: colors[colorIndex % colors.length],
            documentCount: 0,
            // PATCH: Always use ratings from manual skill if present, otherwise from tagData
            ratings: skill.ratings && skill.ratings.length > 0
              ? skill.ratings
              : (tagData ? tagData.ratings : []),
            classifierTypes: skill.classifiers
              ? skill.classifiers.filter(c => c.value).map(c => c.value)
              : [],
            proficiencyLevels: new Set(),
            tag_value_id: skill.tag_value_id || (tagData ? tagData.tag_value_id : null),
            isManual: true,
            _cacheKey: itemsKey
          })
          colorIndex++
        } else {
          // If already present (i.e. added to a document), update descriptor from tags prop if available,
          // but do NOT overwrite ratings with empty array from tagData
          const skillObj = skillMap.get(skill.value)
          if (tagData) {
            skillObj.descriptor = tagData.description
            // Only update ratings if tagData.ratings is non-empty, otherwise preserve manual ratings
            if (tagData.ratings && tagData.ratings.length > 0) {
              skillObj.ratings = tagData.ratings
            }
          } else {
            // fallback to manual skill's ratings/description if not found in tags
            skillObj.ratings = skillObj.ratings && skillObj.ratings.length > 0
              ? skillObj.ratings
              : (skill.ratings || [])
            skillObj.descriptor = skillObj.descriptor || skill.description || ''
          }
        }
      })

      // Convert proficiency levels Set to Array for easier handling
      return Array.from(skillMap.values()).map(skill => ({
        ...skill,
        proficiencyLevels: Array.from(skill.proficiencyLevels)
      }))
    })

    // Simplified filtered skills for legend without async operations
    const filteredSkillsForLegend = computed(() => {
      // Pre-compute focus set for O(1) lookup
      const focusedSet = new Set(focusedSkills.value)
      
      // Use a single pass through skills with minimal operations
      const allSkills = allSkillsWithDescriptors.value
      if (allSkills.length === 0) {
        return []
      }
      
      // Pre-compile filter conditions
      const hasNameFilter = globalFilters.value.nameSearch?.trim()
      const nameSearchTerm = hasNameFilter ? globalFilters.value.nameSearch.toLowerCase() : ''
      
      const hasTypeFilter = globalFilters.value.selectedTypes?.length > 0 && 
                           globalFilters.value.selectedTypes.length < availableSkillTypes.value.length
      const selectedTypesSet = hasTypeFilter ? new Set(globalFilters.value.selectedTypes) : null
      
      const hasProficiencyFilter = globalFilters.value.selectedProficiencyLevels?.length > 0 && 
                                 globalFilters.value.selectedProficiencyLevels.length < availableProficiencyLevels.value.length
      const selectedProficiencySet = hasProficiencyFilter ? new Set(globalFilters.value.selectedProficiencyLevels) : null
      

      
      // Single pass processing with minimal allocations
      const processedSkills = allSkills.map(skill => {
        const isFocused = focusedSet.has(skill.name)
        
        let isMatching = true
        
        // Fastest filters first
        if (hasNameFilter && !skill.name.toLowerCase().includes(nameSearchTerm)) {
          isMatching = false
        } else if (hasTypeFilter && skill.classifierTypes.length > 0) {
          isMatching = skill.classifierTypes.some(type => selectedTypesSet.has(type))
        } else if (isMatching && hasProficiencyFilter && skill.proficiencyLevels.length > 0) {
          isMatching = skill.proficiencyLevels.some(level => selectedProficiencySet.has(level))
        }
        
        return {
          ...skill,
          isMatching,
          isFocused,
          sortPriority: isFocused ? (isMatching ? 0 : 1) : (isMatching ? 2 : 3)
        }
      })
      
      // Sort using pre-computed priorities, then alphabetically by skill name
      return processedSkills.sort((a, b) => {
        if (a.sortPriority !== b.sortPriority) {
          return a.sortPriority - b.sortPriority
        }
        // Alphabetical sort by name (case-insensitive)
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      })
    })

    // Available skill types - unique and sorted
    const availableSkillTypes = computed(() => {
      const allTypes = new Set()
      
      allSkillsWithDescriptors.value.forEach(skill => {
        skill.classifierTypes.forEach(type => allTypes.add(type))
      })
      
      return Array.from(allTypes).sort()
    })

    // Available proficiency levels - unique and sorted
    const availableProficiencyLevels = computed(() => {
      if (!props.tags || props.tags.length === 0) {
        return ['Unrated']
      }
      
      // Find the first tag with ratings data
      const tagWithRatings = props.tags.find(tag => tag.ratings && tag.ratings.length > 0)
      
      if (!tagWithRatings || !tagWithRatings.ratings) {
        return ['Unrated']
      }
      
      // Sort ratings by sequence and extract names
      const sortedRatings = tagWithRatings.ratings
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        .map(rating => rating.name)
      
      // Always include 'Unrated' as the first option if not already present
      if (!sortedRatings.includes('Unrated')) {
        return ['Unrated', ...sortedRatings]
      }
      
      return sortedRatings
    })

    // Check if all types are selected
    const allTypesSelected = computed(() => {
      return availableSkillTypes.value.length > 0 && globalFilters.value.selectedTypes.length === availableSkillTypes.value.length
    })

    // Check if all proficiency levels are selected
    const allProficiencyLevelsSelected = computed(() => {
      return availableProficiencyLevels.value.length > 0 && globalFilters.value.selectedProficiencyLevels.length === availableProficiencyLevels.value.length
    })

    // Check if there are any active filters
    const hasActiveFilters = computed(() => {
      return globalFilters.value.nameSearch !== '' || globalFilters.value.selectedTypes.length > 0 || globalFilters.value.selectedProficiencyLevels.length > 0
    })

    // Get color class based on rating name
    const getRatingColorFromName = (name) => {
      const colors = {
        'Basic': 'bg-gray-100 text-gray-800 border-gray-300',
        'Foundational': 'bg-gray-100 text-gray-800 border-gray-300',
        'Intermediate': 'bg-lime-100 text-lime-800 border-lime-300',
        'Developing': 'bg-lime-100 text-lime-800 border-lime-300',
        'Proficient': 'bg-cyan-100 text-cyan-800 border-cyan-300',
        'Advanced': 'bg-yellow-100 text-yellow-800 border-yellow-300',
        'Expert': 'bg-cyan-100 text-cyan-800 border-cyan-300',
        'Strategic': 'bg-orange-100 text-orange-800 border-orange-300'
      }
      return colors[name] || 'bg-gray-100 text-gray-800 border-gray-300'
    }

    // Toggle all types selection
    const toggleAllTypes = () => {
      if (allTypesSelected.value) {
        globalFilters.value.selectedTypes = []
      } else {
        globalFilters.value.selectedTypes = [...availableSkillTypes.value]
      }
    }

    // Toggle all proficiency levels selection
    const toggleAllProficiencyLevels = () => {
      if (allProficiencyLevelsSelected.value) {
        globalFilters.value.selectedProficiencyLevels = []
      } else {
        globalFilters.value.selectedProficiencyLevels = [...availableProficiencyLevels.value]
      }
    }

    // Clear all selections
    const clearAllSelections = () => {
      selectedItems.value = []
      expandedItems.value = []
      focusedSkills.value = []
    }

    // Clear all filters
    const clearAllFilters = () => {
      globalFilters.value.nameSearch = ''
      globalFilters.value.selectedTypes = []
      globalFilters.value.selectedProficiencyLevels = []
    }

    // Handle item click in the tree
    const handleItemClick = (item) => {
      const idx = selectedItems.value.indexOf(item.id)
      
      if (idx === -1) {
        // Add to selection
        selectedItems.value.push(item.id)
      } else {
        // Remove from selection
        selectedItems.value.splice(idx, 1)
      }
    }

    // Toggle item expansion in the tree
    const toggleExpanded = (itemId) => {
      const idx = expandedItems.value.indexOf(itemId)
      
      if (idx === -1) {
        // Add to expanded
        expandedItems.value.push(itemId)
      } else {
        // Remove from expanded
        expandedItems.value.splice(idx, 1)
      }
    }

    // Handle document data update from child component
    const handleDocumentDataUpdate = ({ key, data }) => {
      
      // Extract document name and reference from the data or key
      let documentName, documentReference
      if (data.documentName && data.documentReference !== undefined) {
        // Direct from data object
        documentName = data.documentName
        documentReference = data.documentReference
      } else if (key) {
        // Parse from key format "documentName|documentReference"
        const keyParts = key.split('|')
        documentName = keyParts[0]
        documentReference = keyParts[1] === 'no-ref' ? '' : keyParts[1]
      } else {
        return
      }
      
      // Find the current document data
      const currentDoc = findDocumentInData(documentName, documentReference)
      if (!currentDoc) {
        return
      }
      
      const docKey = getDocumentKey(documentName, documentReference)
      
      // Store original data if this is the first change for this document
      if (!originalDocumentData.value.has(docKey)) {
        const originalTags = JSON.parse(JSON.stringify(currentDoc.tags))
        originalDocumentData.value.set(docKey, originalTags)
      }
      
      // Update the document state tracking immediately
      nextTick(() => {
        updateDocumentState(documentName, documentReference, currentDoc.tags)
      })
    }

    // Handle items update from child component (for bump chart ordering)
    const handleItemsUpdate = (updatedItems) => {
      
      // Don't just update display order - we need to actually update the data
      // since the bump chart has modified the original data structure
      
      // The updatedItems contain the modified data, so we need to merge it back
      // into our organizational data structure
      itemDisplayOrder.value = updatedItems.map(item => item.id)
      
      // Force reactivity by triggering a state update
      nextTick(() => {
        // Trigger change detection by updating the pending changes
        pendingChanges.value = new Map(pendingChanges.value)
      })
    }

    // Add missing methods that were referenced in the template but not included in return
    const hasMultipleDocuments = (item) => {
      return item.documents && item.documents.length > 1
    }

    const getCardWidth = (item) => {
      const hasMultipleDocs = hasMultipleDocuments(item)
      
      // Standard single document width (18rem = 288px)
      const standardWidth = 350
      const paddingBetweenDocs = 0 // 1rem gap between documents
      const cardPadding = 5 // padding inside the card (p-4 = 1rem each side)
      
      if (item.isDocument || !hasMultipleDocs) {
        return `${standardWidth}px` // Standard width for single documents (w-80)
      }
      
      if (viewMode.value === 'details' && hasMultipleDocs) {
        const numDocs = item.documents?.length || 1
        const totalWidth = (numDocs * standardWidth) + ((numDocs - 1) * paddingBetweenDocs) + cardPadding
        return `${totalWidth}px`
      }
      
      return `${standardWidth}px` // Default fallback (w-80)
    }

    const handleItemDragStart = (event, item, itemIndex) => {
      draggedItem.value = item
      draggedItemIndex.value = itemIndex
      event.dataTransfer.effectAllowed = 'move'
      event.target.style.opacity = '0.5'
      
      // Add dragging class for visual feedback
      event.target.classList.add('item-dragging')
    }

    const handleItemDragOver = (event, index) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      
      if (draggedItemIndex.value !== null && draggedItemIndex.value !== index) {
        dropIndicatorIndex.value = index
      }
    }

    const handleItemDragEnter = (event, index) => {
      event.preventDefault()
      if (draggedItemIndex.value !== null && draggedItemIndex.value !== index) {
        dropIndicatorIndex.value = index
      }
    }

    const handleItemDragLeave = (event, index) => {
      // Only clear if we're leaving the drop zone completely
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX
      const y = event.clientY
      
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        if (dropIndicatorIndex.value === index) {
          dropIndicatorIndex.value = null
        }
      }
    }

    const handleItemDrop = (event, dropIndex) => {
      event.preventDefault()
      dropIndicatorIndex.value = null
      
      if (draggedItemIndex.value !== null && draggedItemIndex.value !== dropIndex) {
        // Get current filtered items and create new order
        const currentItems = [...filteredItems.value]
        const [draggedItemData] = currentItems.splice(draggedItemIndex.value, 1)
        currentItems.splice(dropIndex, 0, draggedItemData)
        
        // Update the display order
        itemDisplayOrder.value = currentItems.map(item => item.id)
      }
    }

    const handleItemDragEnd = (event) => {
      event.target.style.opacity = '1'
      event.target.classList.remove('item-dragging')
      draggedItem.value = null
      draggedItemIndex.value = null
      dropIndicatorIndex.value = null
    }

    const toggleSkillDescriptor = (skillName) => {
      const index = expandedDescriptors.value.indexOf(skillName)
      if (index > -1) {
        expandedDescriptors.value.splice(index, 1)
      } else {
        expandedDescriptors.value.push(skillName)
      }
    }

    // Handle document skill updates from DocumentDetails
    const handleDocumentSkillUpdate = (updateData) => {
      const { documentName, documentReference } = updateData
      
      // Find the current document data using consistent identification
      const currentDoc = findDocumentInData(documentName, documentReference)
      if (!currentDoc) {
        return
      }
      
      const docKey = getDocumentKey(documentName, documentReference)
      
      // Store original data IMMEDIATELY if this is the first change for this document
      if (!originalDocumentData.value.has(docKey)) {
        // Deep clone the current state as original since this is called BEFORE changes are made
        const originalTags = JSON.parse(JSON.stringify(currentDoc.tags))
        originalDocumentData.value.set(docKey, originalTags)
      }
      
      // Use nextTick to allow changes to be applied first, then check for differences
      nextTick(() => {
        updateDocumentState(documentName, documentReference, currentDoc.tags)
      })
    }

    // Helper function to find document in data structure
    const findDocumentInData = (documentName, documentReference) => {
      let foundDoc = null
      
      const searchInData = (data) => {
        data.forEach(item => {
          if (item.documents) {
            const doc = item.documents.find(d => {
              const docName = d.jobFamily || d.name
              const docRef = d.reference || ''
              return docName === documentName && docRef === documentReference
            })
            if (doc) {
              foundDoc = doc
            }
          }
          
          if (item.children && !foundDoc) {
            searchInData(item.children)
          }
        })
      }
      
      searchInData(props.organizationalData)
      return foundDoc
    }

    // Global revert all changes
    const revertAllChanges = () => {
      // Restore all documents to their original state
      const restoreDocuments = (data) => {
        data.forEach(item => {
          if (item.documents) {
            item.documents.forEach(doc => {
              const docName = doc.jobFamily || doc.name
              const docRef = doc.reference || ''
              const docKey = getDocumentKey(docName, docRef)
              const originalTags = originalDocumentData.value.get(docKey)
              
              if (originalTags) {
                // Deep clone and sanitize tags to remove any lingering DELETE_SKILL values
                const restoredTags = JSON.parse(JSON.stringify(originalTags))
                restoredTags.forEach(tag => {
                  // If the tag was previously deleted and has a _previousName, restore it
                  if (tag.name === 'DELETE_SKILL') {
                    if (tag._previousName && tag._previousName !== 'DELETE_SKILL' && tag._previousName !== null) {
                      tag.name = tag._previousName
                      // Optionally restore tag_type_rating_id if needed (if available in your data)
                    } else {
                      tag.name = null
                    }
                    tag.tag_type_rating_id = null
                  }
                  if (tag._previousName === 'DELETE_SKILL') {
                    tag._previousName = null
                  }
                })
                doc.tags = restoredTags
              }
            })
          }
          
          if (item.children) {
            restoreDocuments(item.children)
          }
        })
      }
      
      restoreDocuments(props.organizationalData)

      // Clear all pending changes and force reactivity update
      pendingChanges.value.clear()
      originalDocumentData.value.clear()

      // Force immediate reactivity update by creating new Map instances
      pendingChanges.value = new Map()
      originalDocumentData.value = new Map()

      // --- Force rerender of DocumentDetails after revert ---
      rerenderKey.value++
    }

    // Save all changes functionality - updated to use notifications and refetch data
    const saveAllChanges = async () => {
      isSaving.value = true; // Show saving overlay
      try {
        // Map pending changes to the requested structure
        const documentsToUpdate = []
        
        pendingChanges.value.forEach((changeInfo, docKey) => {
          const { documentName, documentReference } = changeInfo
          
          // Find the current document data
          const currentDoc = findDocumentInData(documentName, documentReference)
          if (currentDoc) {
            const documentUpdate = {
              doc_id: currentDoc.doc_id,
              dp_id: currentDoc.dp_id,
              dpa_id: currentDoc.dpa_id,
              tpa_id: currentDoc.tpa_id,
              dp_name: currentDoc.dp_name,
              tag_type_id: currentDoc.tag_type_id,
              tp_id: currentDoc.tp_id,
              doc_refrence: documentReference,
              tagList: currentDoc.tags.map(tag => ({
                // For new skills with temporary IDs, send null to indicate new skill
                tag_value_id: (tag.isNewSkill || String(tag.tag_value_id).startsWith('new_')) ? null : tag.tag_value_id,
                value: tag.value,
                name: tag.name,
                tag_type_rating_id: tag.tag_type_rating_id,
                sequence: tag.sequence,
                // Ensure tag_type_id is included in each tag
                tag_type_id: tag.tag_type_id || currentDoc.tag_type_id,
                isDeleted: tag.isDeleted || false
              }))
            }
            
            documentsToUpdate.push(documentUpdate)
          }
        })
  
        // Send the mapped data to the API
        await axios.post('skills/skillManager/updateDocs', documentsToUpdate);
        triggerNotification("Skills Updated Successfully", "success")
        
        // Clear all pending changes and force reactivity update
        pendingChanges.value.clear()
        originalDocumentData.value.clear()
        
        // Force immediate reactivity update by creating new Map instances
        pendingChanges.value = new Map()
        originalDocumentData.value = new Map()

        // Update local data without full refetch to prevent screen reload
        await updateLocalDataAfterSave()
        
      } catch (err) {
        console.log(err)
        triggerNotification("Error saving changes: " + err.message, "error")
      } finally {
        isSaving.value = false; // Hide saving overlay
      }
    }

    // New function to update local data after save without full refetch
    const updateLocalDataAfterSave = async () => {
      try {
        // Update local data by removing isNewSkill flags and ensuring proper tag_value_ids
        const updateDocuments = (data) => {
          data.forEach(item => {
            if (item.documents) {
              item.documents.forEach(doc => {
                if (doc.tags) {
                  doc.tags.forEach(tag => {
                    // Remove temporary flags and update tag_value_ids for new skills
                    if (tag.isNewSkill || String(tag.tag_value_id).startsWith('new_')) {
                      // Find the corresponding tag data from props.tags to get the real tag_value_id
                      const tagData = props.tags.find(t => t.value === tag.value)
                      if (tagData && tagData.tag_value_id) {
                        tag.tag_value_id = tagData.tag_value_id
                      }
                      // Remove temporary flags
                      delete tag.isNewSkill
                    }
                    
                    // Clean up any DELETE_SKILL markers or temporary flags
                    if (tag.name === 'DELETE_SKILL') {
                      // This skill should be removed from the UI
                      tag.isDeleted = true
                    }
                    
                    // Remove any temporary properties
                    delete tag._previousName
                  })
                  
                  // Filter out deleted skills from the UI
                  doc.tags = doc.tags.filter(tag => !tag.isDeleted)
                }
              })
            }
            
            if (item.children) {
              updateDocuments(item.children)
            }
          })
        }
        
        // Update the organizational data in place
        updateDocuments(props.organizationalData)
        
        // Force reactivity update without full reload
        nextTick(() => {
          // Trigger a minimal reactive update
          rerenderKey.value++
        })
        
      } catch (error) {
        console.error('Error updating local data after save:', error)
        // Fallback to full refresh if local update fails
        await refetchDataAfterSave()
      }
    }

    // New function to refetch data while preserving UI state
    const refetchDataAfterSave = async () => {
      try {
        // Store current UI state before refetch
        const currentUIState = {
          selectedItems: [...selectedItems.value],
          expandedItems: [...expandedItems.value],
          focusedSkills: [...focusedSkills.value],
          itemDisplayOrder: [...itemDisplayOrder.value],
          expandedDescriptors: [...expandedDescriptors.value],
          globalFilters: JSON.parse(JSON.stringify(globalFilters.value)),
          viewMode: viewMode.value,
          sidebarTab: sidebarTab.value,
          minDocumentCount: minDocumentCount.value
        }
        
        // Emit refresh event to parent component to reload props
        // The parent should re-fetch organizationalData and tags
        emit('refresh-data')
        
        // Wait for next tick to ensure parent has updated props
        await nextTick()
        
        // Restore UI state after data refresh
        selectedItems.value = currentUIState.selectedItems
        expandedItems.value = currentUIState.expandedItems
        focusedSkills.value = currentUIState.focusedSkills
        itemDisplayOrder.value = currentUIState.itemDisplayOrder
        expandedDescriptors.value = currentUIState.expandedDescriptors
        globalFilters.value = currentUIState.globalFilters
        viewMode.value = currentUIState.viewMode
        sidebarTab.value = currentUIState.sidebarTab
        minDocumentCount.value = currentUIState.minDocumentCount
        
      } catch (error) {
        console.error('Error during data refetch:', error)
        triggerNotification("Data refreshed but some UI state may have been lost", "warning")
      }
    }

    // Helper function to create document key
    const getDocumentKey = (documentName, documentReference) => {
      return `${documentName}|${documentReference || 'no-ref'}`
    }

    // Update document state tracking
    const updateDocumentState = (documentName, documentReference, currentTags) => {
      const docKey = getDocumentKey(documentName, documentReference)
      
      // Get original state
      const originalTags = originalDocumentData.value.get(docKey)
      if (!originalTags) {
        return
      }
      
      // Create current document state summary
      const currentState = createDocumentStateSummary(currentTags)
      const originalState = createDocumentStateSummary(originalTags)
      
      // Compare states to determine if there are changes
      const hasChanges = !areDocumentStatesEqual(originalState, currentState)
      
      if (hasChanges) {
        // Store the current state with change details
        pendingChanges.value.set(docKey, {
          documentName,
          documentReference,
          originalState,
          currentState,
          lastModified: new Date().toISOString()
        })
        
        // Trigger reactivity
        pendingChanges.value = new Map(pendingChanges.value)
      } else {
        // No changes, remove from pending if it exists
        if (pendingChanges.value.has(docKey)) {
          pendingChanges.value.delete(docKey)
          pendingChanges.value = new Map(pendingChanges.value)
        }
      }
    }

    // Create a state summary for a document's tags
    const createDocumentStateSummary = (tags) => {
      const skillsMap = new Map()
      
      tags.forEach(tag => {
        skillsMap.set(tag.value, {
          name: tag.value,
          sequence: tag.sequence || 1,
          proficiency: tag.name || 'Unrated',
          tagValueId: tag.tag_value_id
        })
      })
      
      return {
        skillCount: tags.length,
        skills: skillsMap,
        skillsList: Array.from(skillsMap.keys()).sort(),
        priorityOrder: tags
          .sort((a, b) => (a.sequence || 1) - (b.sequence || 1))
          .map(tag => tag.value)
      }
    }

    // Compare two document states to determine if they're equal
    const areDocumentStatesEqual = (state1, state2) => {
      // Check skill count
      if (state1.skillCount !== state2.skillCount) return false
      
      // Check if same skills exist
      if (state1.skillsList.length !== state2.skillsList.length) return false
      if (!state1.skillsList.every(skill => state2.skillsList.includes(skill))) return false
      
      // Check priority order
      if (state1.priorityOrder.length !== state2.priorityOrder.length) return false
      if (!state1.priorityOrder.every((skill, index) => state2.priorityOrder[index] === skill)) return false
      
      // Check individual skill details (proficiency, etc.)
      for (const [skillName, skill1] of state1.skills) {
        const skill2 = state2.skills.get(skillName)
        if (!skill2) return false
        
        if (skill1.sequence !== skill2.sequence) return false
        if (skill1.proficiency !== skill2.proficiency) return false
        if (skill1.tagValueId !== skill2.tagValueId) return false
      }
      
      return true
    }

    // Check if a document has pending changes by document key
    const hasDocumentPendingChangesByKey = (docKey) => {
      return pendingChanges.value.has(docKey)
    }

    // Computed properties for pending changes
    const hasPendingChanges = computed(() => {
      return pendingChanges.value.size > 0
    })

    const pendingChangesCount = computed(() => {
      return pendingChanges.value.size
    })

    // Custom labels for filter selects
    const skillTypesLabel = computed(() => {
      const selectedCount = globalFilters.value.selectedTypes.length
      const totalCount = availableSkillTypes.value.length
      
      if (selectedCount === 0 || selectedCount === totalCount) {
        return 'All'
      } else if (selectedCount === 1) {
        return globalFilters.value.selectedTypes[0]
      } else {
        return `${selectedCount} selected`
      }
    })

    const proficiencyLevelsLabel = computed(() => {
      const selectedCount = globalFilters.value.selectedProficiencyLevels.length
      const totalCount = availableProficiencyLevels.value.length
      
      if (selectedCount === 0 || selectedCount === totalCount) {
        return 'All'
      } else if (selectedCount === 1) {
        return globalFilters.value.selectedProficiencyLevels[0]
      } else {
        return `${selectedCount} selected`
      }
    })

    // Refresh all data functionality
    const refreshAllData = async () => {
      if (hasPendingChanges.value) {
        const confirmed = confirm('You have unsaved changes. Refreshing will lose all pending changes. Are you sure you want to continue?')
        if (!confirmed) {
          return
        }
      }

      isRefreshing.value = true
      
      try {
        // Clear all pending changes and state first
        pendingChanges.value.clear()
        originalDocumentData.value.clear()
        selectedItems.value = []
        expandedItems.value = []
        focusedSkills.value = []
        itemDisplayOrder.value = []
        expandedDescriptors.value = []
        
        // Reset global filters
        globalFilters.value = {
          nameSearch: '',
          selectedTypes: [],
          selectedProficiencyLevels: []
        }
        
        // Reset view mode to default
        viewMode.value = 'details'
        sidebarTab.value = 'skills'
        
        // Force reactive updates
        pendingChanges.value = new Map()
        originalDocumentData.value = new Map()
        
        // Emit refresh event to parent component to reload props
        emit('refresh-data')
        
      } catch (error) {
        alert('Error refreshing data. Please try again.')
      } finally {
        isRefreshing.value = false
      }
    }

    // Add this method to handle toggling skill focus from DocumentDetails
    const handleToggleSkillFocus = (skillName) => {
      const idx = focusedSkills.value.indexOf(skillName)
      if (idx > -1) {
        focusedSkills.value = focusedSkills.value.filter(s => s !== skillName)
      } else {
        focusedSkills.value = [...focusedSkills.value, skillName]
      }
    }

    // Show Add Skills Dialog
    const showAddSkillsDialog = ref(false)
    // Use a Set to avoid duplicate manual skills by value
    const manuallyAddedSkills = ref([])
    
    // Hierarchy filtering state - initialize with props data
    const preSelectedHierarchyFilters = ref([])
    const preSelectedClassifierFilters = ref([])
    const allHierarchyItems = ref([])

    // Computed property to get documents with hierarchy data from store that match the organizational data
    const passedParamsDocuments = computed(() => {
      // First, extract document IDs from organizational data
      const orgDocumentIds = []
      
      const extractDocumentIds = (data) => {
        data.forEach(item => {
          if (item.documents && item.documents.length > 0) {
            item.documents.forEach(doc => {
              orgDocumentIds.push(doc.doc_id)
            })
          }
          if (item.children && item.children.length > 0) {
            extractDocumentIds(item.children)
          }
        })
      }
      
      if (props.organizationalData && props.organizationalData.length > 0) {
        extractDocumentIds(props.organizationalData)
      }
      
      // Now find matching documents from the store that have hierarchy data
      const storeDocuments = store.state.docs?.docsList || []
      const matchingDocuments = storeDocuments.filter(doc => 
        orgDocumentIds.includes(doc.doc_id)
      )
      
      return matchingDocuments
    })

    const tagTypeIdForAddSkills = computed(() => {
      return props.tags && props.tags.length > 0 ? props.tags[0].tag_type_id : null
    })

    // Toggle filters visibility
    const toggleFilters = () => {
      showFilters.value = !showFilters.value
      console.log('Filters toggled:', showFilters.value)
    }

    // Hierarchy filtering method
    const doHierarchyFilter = (hierarchyFilters, classifierFilters) => {
      preSelectedHierarchyFilters.value = hierarchyFilters
      preSelectedClassifierFilters.value = classifierFilters
    }

    // Helper function to check for documents in organizational data
    const checkForDocuments = (data) => {
      if (!data || data.length === 0) return false
      
      for (const item of data) {
        if (item.documents && item.documents.length > 0) {
          return true
        }
        if (item.children && checkForDocuments(item.children)) {
          return true
        }
      }
      return false
    }

    // Set initial sidebar tab based on whether documents are available
    const setInitialSidebarTab = () => {
      // Only set the default tab if we haven't initialized it yet
      if (hasInitializedSidebarTab.value) {
        return
      }

      // Check if we have any documents in the organizational data directly
      const hasDocuments = checkForDocuments(props.organizationalData)
      
      if (hasDocuments) {
        sidebarTab.value = 'skills'
      } else {
        sidebarTab.value = 'hierarchy'
      }
      
      // Mark as initialized so we don't override user's choice later
      //hasInitializedSidebarTab.value = true
    }

    // Watch for changes in organizational data to trigger initial setup
    watch(() => props.organizationalData, () => {
      // Only auto-switch if we haven't initialized the sidebar tab yet
      if (!hasInitializedSidebarTab.value) {
        setInitialSidebarTab()
      }
    }, { immediate: true, deep: true })

    // Populate hierarchy items from the store when component mounts
    onMounted(() => {
      // Set initial sidebar tab based on available data
      setInitialSidebarTab()
      
      // Use a watcher to monitor changes to the cleanItems from the store
      watch(() => store.state.docs.docsList, (newItems) => {
        allHierarchyItems.value = Array.isArray(newItems) ? newItems : []
      }, { immediate: true })
    })

    // Handle explore documents from SkillHierarchySearch and emit to parent (SkillWorkspace)
    const handleExploreDocuments = (selectedDocuments) => {
      sidebarTab.value = 'skills';
      // Emit to parent SkillWorkspace to connect with handleExploreSkills functionality
      emit('exploreSkills', selectedDocuments)
    }
    
    // Watch for view mode changes and reset scroll position for chart/matrix views
    watch(() => viewMode.value, (newMode, oldMode) => {
      // Reset scroll position when switching to chart or matrix view
      if ((newMode === 'bumpChart' || newMode === 'matrix') && oldMode === 'details') {
        nextTick(() => {
          // Find the main content area and reset its scroll position
          const mainContentArea = document.querySelector('.main-content-area')
          if (mainContentArea) {
            mainContentArea.scrollLeft = 0
          }
          
          // Also reset any chart/matrix container scroll positions
          const chartContainer = document.querySelector('.chart-view-container')
          const matrixContainer = document.querySelector('.matrix-view-container')
          
          if (chartContainer) {
            chartContainer.scrollLeft = 0
            chartContainer.scrollTop = 0
          }
          
          if (matrixContainer) {
            matrixContainer.scrollLeft = 0
            matrixContainer.scrollTop = 0
          }
        })
      }
    })

    return {
      selectedItems,
      viewMode,
      expandedItems,
      sidebarTab,
      focusedSkills,
      draggedItem,
      draggedItemIndex,
      itemDisplayOrder,
      dropIndicatorIndex,
      expandedDescriptors,
      pendingChanges,
      originalDocumentData,
      globalFilters,
      minDocumentCount,
      treeData,
      allItems,
      filteredItems,
      totalDocumentCount,
      allSkillsWithDescriptors,
      filteredSkillsForLegend,
      availableSkillTypes,
      availableProficiencyLevels,
      allTypesSelected,
      allProficiencyLevelsSelected,
      hasActiveFilters,
      hasPendingChanges,
      pendingChangesCount,
      skillTypesLabel,
      proficiencyLevelsLabel,
      getRatingColorFromName,
      toggleAllTypes,
      toggleAllProficiencyLevels,
      clearAllSelections,
      clearAllFilters,
      handleItemClick,
      toggleExpanded,
      handleDocumentDataUpdate,
      handleItemsUpdate,
      saveAllChanges,
      revertAllChanges,
      hasDocumentPendingChangesByKey,
      onLegendSkillClick,
      handleSkillFocus,
      toggleSkillDescriptor,
      hasMultipleDocuments,
      getCardWidth,
      handleItemDragStart,
      handleItemDragOver,
      handleItemDragEnter,
      handleItemDragLeave,
      handleItemDrop,
      handleItemDragEnd,
      handleDocumentSkillUpdate,
      findDocumentInData,
      getDocumentKey,
      updateDocumentState,
      createDocumentStateSummary,
      areDocumentStatesEqual,
      isRefreshing,
      refreshAllData,
      notification,
      isSaving,
      viewNodeDoc,
      documentSummary,
      updateLocalDataAfterSave,
      refetchDataAfterSave,
      rerenderKey,
      openDocFromPreview,
      handleToggleSkillFocus,
      showAddSkillsDialog,
      tagTypeIdForAddSkills,
      handleAddSkillsSave,
      manuallyAddedSkills,
      isSidebarCollapsed,
      showFilters,
      toggleFilters,
      preSelectedHierarchyFilters,
      preSelectedClassifierFilters,
      allHierarchyItems,
      passedParamsDocuments,
      doHierarchyFilter,
      handleExploreDocuments,
      setInitialSidebarTab,
      checkForDocuments
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200');

.organizational-skills-tree {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  height: 100%;
  overflow-x: hidden;
}

/* Override all heading tags to use DM Sans */
.organizational-skills-tree h1,
.organizational-skills-tree h2,
.organizational-skills-tree h3,
.organizational-skills-tree h4,
.organizational-skills-tree h5,
.organizational-skills-tree h6 {
  font-family: inherit !important;
}

.organizational-skills-tree > div {
  min-width: max-content;
}

/* Content height constraint */
.content-max-height {
  max-height: calc(100vh - 155px);
}

/* Layout classes */
.jobLeft { 
  flex: 0 0 330px; 
  min-width: 330px;
  max-height: calc(100vh - 155px);
  transition: min-width 0.3s ease, width 0.3s ease;
 }

.jobLeft.collapsed {
  min-width: 50px;
  width: 50px;
}

.jobRight { 
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-height: calc(100vh - 138px);
  padding: 25px 40px 0px 28px;
  gap: 10px;
  background: #FFFFFF;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 24px;
  flex: 1;
  order: 2;
  align-self: stretch;
  flex-grow: 1;
  width: calc(100vw - 410px);
  max-width: calc(100vw - 410px);
  overflow-x: auto;
  transition: margin-left 0.3s ease, width 0.3s ease, max-width 0.3s ease;
}

/* Apply margin only when sidebar is not collapsed */
.sidebar:not(.collapsed) + .jobRight {
  margin-left: 40px;
}

.sidebar.collapsed + .jobRight {
  margin-left: 0px;
  width: calc(100vw - 90px);
  max-width: calc(100vw - 90px);
}

.rightContentHeader {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-direction: row;
  max-width: 100vw;
  margin-bottom: 0px;
}

.main-content-area {
  flex: 1;
  width: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  max-height: calc(100vh - 200px);
  position: relative; /* Added for absolute positioning of loading overlay */
}

/* Only apply horizontal scroll for Document View */
.main-content-area.document-view {
  overflow-x: scroll;
}

/* Chart View Container - Isolated from document view scrolling */
.chart-view-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  /* Reset any inherited scroll positions */
  scroll-behavior: auto;
}

/* Matrix View Container - Isolated from document view scrolling */
.matrix-view-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  /* Reset any inherited scroll positions */
  scroll-behavior: auto;
}

/* Document View Container - Maintains horizontal scrolling */
.document-view-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: start;
  overflow-x: auto;
  overflow-y: hidden;
}

/* Sidebar - Hierarchy Navigation & Skills Legend Tabs */
.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  width: 330px;
  background: #FFFFFF;
  border-width: 1px 1px 1px 0px;
  border-style: solid;
  border-color: #DDDDDD;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 0px 24px 24px 0px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;
  position: relative;
}

.sidebar.collapsed {
  width: 50px;
  min-width: 50px;
  overflow: hidden;
  background: transparent;
  border: none;
  box-shadow: none;
}

/* Button */
.sidebar-toggle-btn-floating {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 5px;
  gap: 4px;
  position: absolute;
  width: 32px;
  height: 40px;
  left: 330px;
  top: 98px;
  background: #EEEEEE;
  border-width: 1px 0px 1px 1px;
  border-style: solid;
  border-color: #DDDDDD;
  border-radius: 20px 0px 0px 20px;
  transform: rotate(-180deg);
  flex: none;
  order: 1;
  flex-grow: 0;
  z-index: 1;
  cursor: pointer;
}

.sidebar-toggle-btn-floating:hover {
  background: #E0E0E0;
}

.sidebar-toggle-btn-floating .v-icon {
  color: #6b7280;
}

.sidebar-toggle-btn-floating:hover .v-icon {
  color: #3b82f6;
}

/* Collapsed state positioning */
.sidebar.collapsed .sidebar-toggle-btn-floating {
  left: 0px;
  transform: rotate(0deg);
  border-radius: 0px 20px 20px 0px;
  border-width: 1px 1px 1px 0px;
}

.sidebar-toggle-container {
  flex-shrink: 0;
  height: 44px;
}

.sidebar-toggle-btn {
  border: 1px solid transparent;
}

.sidebar-toggle-btn:hover {
  background-color: #e5e7eb;
  border-color: #d1d5db;
}

.sidebar-toggle-btn .v-icon {
  transition: transform 0.3s ease;
}
.sidebar-tabs {
  display: flex;
  flex-shrink: 0;
  height: 58px;
  max-height: 58px;
  padding-left: 8px;
}

.sidebar-tab {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 20px;
  gap: 4px;
  width: 157px;
  height: 58px;
  border: none;
  border-bottom: 6px solid transparent;
  cursor: pointer;
  flex: none;
  order: 0;
  flex-grow: 1;
  font-weight: 600;
  color: #AAAAAA;
  transition: background 0.2s, color 0.2s;
  font-weight: 700;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
}

.sidebar-tab.active {
  color: #000000;
  border-bottom: 6px solid #48A401;
}

/* Icon color inheritance */
.sidebar-tab .v-icon {
  color: #AAAAAA !important;
}

.sidebar-tab.active .v-icon {
  color: #000000 !important;
}

.sidebar-container{
  border-top:1px solid #DDD;
  padding-left: 16px;
}

.sidebar-container.hierarchy-padding {
  padding-left: 0px !important;
}

/* Material Icons styling */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 18px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

/* Skills Legend Styles */
.legend-list {
  max-height: calc(100vh - 520px);
  overflow-y: auto;
  transition: max-height 0.2s ease;
  padding-right: 5px;
}

.legend-header {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px;
  gap: 8px;
  height: 34px;
  background: rgba(238, 238, 238, 0.5);
  border-bottom: 1px solid #DDDDDD;
  border-radius: 8px 8px 0px 0px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  vertical-align: middle;

}

.legend-list.filters-hidden {
  max-height: calc(100vh - 440px);
}

.legend-list.filters-shown {
  max-height: calc(100vh - 685px);
}

/* Optimized legend item styles for better performance */
.legend-item-container {
  border: 1px solid transparent;
  border-radius: 4px;
  margin-bottom: 2px;
  /* Reduced transition time for faster response */
  transition: all 0.1s ease-out;
  /* Use transform instead of scale for better performance */
  will-change: transform, opacity, filter;
}

.legend-item-container:hover {
  background: #f9fafb;
  border-color: #e5e7eb;
}

.legend-item-container.focused {
  background: #e0f2fe;
  border: 1px solid #3b82f6;
}

.legend-item-container.unfocused {
  opacity: 0.3;
  /* Reduce filter complexity for better performance */
  filter: grayscale(30%);
  border: 1px solid transparent;
}

.legend-item-container.unfocused:hover {
  opacity: 0.6;
  filter: grayscale(10%);
}

.legend-item-container.non-matching {
  opacity: 0.4;
  /* Lighter filter for better performance */
  filter: grayscale(20%);
 }

.legend-item-container.non-matching:hover {
  opacity: 0.6;
  filter: grayscale(5%);
}

.legend-item {
  display: flex;
  align-items: center;
  padding: 8px;
  cursor: pointer;
}

.legend-expand {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  border-radius: 3px;
  transition: background 0.2s;
}

.legend-expand:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.accordion-content {
  margin: 0 8px 8px 8px;
  border-radius: 0 0 4px 4px;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 8px;
}

/* Optimize legend text rendering */
.legend-text {
  flex: 1;
  font-size: 14px;
  color: #000000;
  /* Optimize text rendering */
  text-rendering: optimizeSpeed;
  font-display: swap;
}

.skill-count {
  font-size: 12px;
  color: #AAAAAA;
  margin-left: 4px;
}

.legend-indicator {
  color: #3B82F6;
  margin-left: 8px;
}

/* Optimized tree view styles */
.tree-node {
  transition: background 0.2s ease, transform 0.2s ease;
  will-change: transform, opacity;
  transform: translateZ(0);
}

.tree-node:hover {
  background: #f1f5f9;
  transform: translateY(-1px);
}

.tree-node.selected {
  background: #d1e7dd;
  border-color: #0f5132;
  box-shadow: 0 0 0 2px rgba(15, 81, 50, 0.2);
}

.tree-node.expanded {
  font-weight: 500;
  color: #111827;
}

/* Document details styles */
.document-details {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.document-details-enter-active, .document-details-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.document-details-enter, .document-details-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Bump chart styles */
.bump-chart {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.bump-chart-enter-active, .bump-chart-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.bump-chart-enter, .bump-chart-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Header button styles */
.header-button {
  transition: background 0.2s ease, color 0.2s ease;
}

.header-button:hover {
  background: #f3f4f6;
  color: #111827;
}

/* Custom scrollbar styles */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}

::-webkit-scrollbar-track {
  background-color: #f9fafb;
  border-radius: 4px;
}

.w-full { width: 100%; }
.h-screen { height: 100%; }
.bg-gray-100 { background-color: #f3f4f6; }
.flex { display: flex; }
.w-80 { width: 10rem; }
.bg-white { background-color: #ffffff; }
.border-r { border-right-width: 1px; }
.border-gray-200 { border-color: #e5e7eb; }
.flex-col { flex-direction: column; }
.flex-shrink-0 { flex-shrink: 0; }
.flex-1 { flex: 1 1 0%; }
.min-w-0 { min-width: 0px; }

/* Spacing classes */
.p-1 { padding: 0.25rem; }
.p-2 { padding: 0.5rem; }
.p-3 { padding: 0.75rem; }
.p-4 { padding: 1rem; }
.p-6 { padding: 1.5rem; }
.px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
.px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
.px-4 { padding-left: 1rem; padding-right: 1rem; }
.py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.py-0\.5 { padding-top: 0.125rem; padding-bottom: 0.125rem; }
.py-12 { padding-top: 3rem; padding-bottom: 3rem; }
.mt-1 { margin-top: 0.25rem; }
.mt-2 { margin-top: 0.5rem; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-4 { margin-bottom: 1rem; }

/* Gap classes */
.gap-1 { gap: 0.25rem; }
.gap-2 { gap: 0.5rem; }
.gap-3 { gap: 0.75rem; }
.gap-4 { gap: 1rem; }
.space-y-1 > * + * { margin-top: 0.25rem; }
.space-y-2 > * + * { margin-top: 0.5rem; }
.space-y-3 > * + * { 
  margin-top: 0.6rem; /* Reduced from 0.75rem */
}
.space-y-4 > * + * { margin-top: 1rem; }

/* Border classes */
.border { border-width: 1px; }
.border-b { border-bottom-width: 1px; }
.border-t { border-top-width: 1px; }
.border-gray-200 { border-color: #e5e7eb; }
.rounded { border-radius: 0.25rem; }
.rounded-md { border-radius: 0.375rem; }
.rounded-lg { border-radius: 0.5rem; }

/* Typography classes */
.text-xs { font-size: 0.75rem; line-height: 1rem; }
.text-sm { font-size: 0.875rem; line-height: 1.25rem; }
.text-lg { font-size: 1.125rem; line-height: 1.75rem; }
.text-2xl { font-size: 1.5rem; line-height: 2rem; }
.text-6xl { font-size: 3.75rem; line-height: 1; }
.font-medium { font-weight: 500; }
.font-semibold { font-weight: 600; }
.font-bold { font-weight: 700; }
.leading-tight { line-height: 1.25; }

/* Color classes */
.text-gray-500 { color: #6b7280; }
.text-gray-600 { color: #4b5563; }
.text-gray-700 { color: #374151; }
.text-gray-900 { color: #111827; }
.text-blue-600 { color: #2563eb; }
.text-orange-600 { color: #ea580c; }
.text-purple-600 { color: #9333ea; }
.text-purple-700 { color: #7c3aed; }
.text-green-700 { color: #15803d; }
.text-yellow-700 { color: #a16207; }
.bg-gray-50 { background-color: #f9fafb; }
.bg-gray-100 { background-color: #f3f4f6; }
.bg-green-100 { background-color: #dcfce7; }
.bg-yellow-100 { background-color: #fef3c7; }
.bg-cyan-100 { background-color: #50b3cb; } 
.text-cyan-800 { color: #0b232c; } 
.border-cyan-300 { border-color: #1b9dd1; }

.bg-lime-100 { background-color: #cedc26; } 
.text-lime-800 { color: #0b2c0e; } 
.border-lime-300 { border-color: #099628; }

/* Layout utilities */
.items-center { align-items: center; }
.items-start { align-items: flex-start; }
.justify-between { justify-content: space-between; }
.text-center { text-align: center; }
.overflow-y-auto { overflow-y: auto; }
.overflow-x-auto { overflow-x: auto; }
.overflow-y-hidden { overflow-y: hidden; }
.h-full { height: 100%; }
.min-w-max { min-width: max-content; height:100% }
.w-64 { width: 16rem; }
.w-80 { width: 20rem; }

/* Interactive classes */
.cursor-pointer { cursor: pointer; }
.hover\:text-blue-800:hover { color: #1e40af; }
.hover\:bg-gray-200:hover { background-color: #e5e7eb; }
.underline { text-decoration-line: underline; }
.transition-colors { 
  transition-property: color, background-color, border-color; 
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); 
  transition-duration: 150ms; 
}
.transition-all { transition-property: all; }
.duration-300 { transition-duration: 300ms; }

/* Positioning */
.sticky { position: sticky; }
.top-0 { top: 0px; }
.z-10 { z-index: 10; }

/* Shadow */
.shadow-sm { box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); }

/* Draggable item styles */
.item-card {
  transition: all 0.2s ease;
  cursor: default;
  position: relative;
  border: 1px solid #EEEEEE;
}

.item-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.item-card.item-dragging {
  z-index: 1000;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  opacity: 0.5 !important;
  transform: rotate(1deg);
  border: 1px solid #EEEEEE;
}

.item-card.drop-zone-active {
  border: 2px dashed #3b82f6;
  background-color: #eff6ff;
  transform: scale(1.02);
}

/* Drop indicator styles for item reordering */
.item-card .drop-indicator {
  position: absolute;
  left: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(to bottom, #3b82f6, #1d4ed8);
  border-radius: 2px;
  z-index: 1001;
  box-shadow: 0 0 8px rgba(0, 130, 246, 0.6);
}

.item-card .drop-indicator::before {
  content: '';
  position: absolute;
  top: -6px;
  left: -4px;
  width: 12px;
  height: 12px;
  background: #3b82f6;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(0, 130, 246, 0.8);
}

.item-card .drop-indicator::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: -4px;
  width: 12px;
  height: 12px;
  background: #3b82f6;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(0, 130, 246, 0.8);
}

/* Prevent nested dragging when item is being dragged */
.item-dragging .skill-item {
  pointer-events: none;
  opacity: 0.7;
}

.item-dragging .document-card {
  pointer-events: none;
  opacity: 0.7;
}

/* Accordion styles */
.rotate-180 {
  transform: rotate(180deg);
}

.transition-transform {
  transition: transform 0.2s ease;
}

.bg-gray-100 { background-color: #FBFBFB; }
.text-gray-800 { color: #1f2937; }
.border-gray-300 { border-color: #d1d5db; }
.bg-blue-100 { background-color: #dbeafe; }
.text-blue-800 { color: #1e40af; }
.border-blue-300 { border-color: #93c5fd; }
.bg-green-100 { background-color: #dcfce7; }
.text-green-800 { color: #166534; }
.border-green-300 { border-color: #86efac; }
.bg-purple-100 { background-color: #f3e8ff; }
.text-purple-800 { color: #6b21a8; }
.border-purple-300 { border-color: #c4b5fd; }
.bg-orange-100 { background-color: #fed7aa; }
.text-orange-800 { color: #9a3412; }
.border-orange-300 { border-color: #fdba74; }

/* Pending changes indicator */
.pending-changes-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fef3c7;
  border:  1px solid #f59e0b;
  animation: pulse-warning 2s infinite;
}

@keyframes pulse-warning {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

/* General button styles */
.organizational-skills-tree .v-btn {
  background-color: #eee !important;
  padding: 8px 10px !important;
  min-width: auto !important;
  height: auto !important;
  border-radius: 8px;
}

.organizational-skills-tree .v-btn:disabled {
  color: #888888 !important;
  background-color: #eee !important;
  opacity: 1 !important;
}

.organizational-skills-tree .v-btn .v-icon {
  margin-right: 4px !important;
}

.revert-btn {
  font-size: 12px !important;
}

.refresh-btn {
  font-size: 12px !important;
}

.save-btn {
  font-size: 12px !important;
}

/* Filters button tab-like appearance when expanded */
.filters-expanded {
  height: 40px !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  margin-bottom: -8px !important;
  align-items: flex-start !important;
  padding-top: 4px !important;
}

/* Filter section styles */
.filters-section {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 12px;
  margin-top:8px;
  border-top-right-radius: 0px;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-label {
  display: block;
  margin-bottom: 4px;
}

.skill-search-field {
  font-size: 12px;
}

.skill-types-filter,
.proficiency-levels-filter {
  font-size: 12px;
}

.filter-summary {
  padding-top: 8px;
  margin-top: 8px;
}

/* Saving overlay styles */
.saving-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.saving-message {

  text-align: center;
  font-size: 18px;
  color: #374151;
}


/* Header Styles */

.sub-header-style-1 {
  font-weight: 700;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  vertical-align: middle;
}

.actionsContainer{
  width: 100%;
}

.view-controls {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding: 3px;
  margin: 0;
  width: 418px;
  height: 34px;
  background: #EEEEEE;
  border: 1px solid #DDDDDD;
  border-radius: 11px;
  flex: none;
  order: 1;
  flex-grow: 0;
}

.view-control {
  padding: 4px 12px;
  margin: 0 2px;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.view-control:hover {
  background: rgba(255, 255, 255, 0.5);
}

.view-control.active {
  background: #FFFFFF;
  color: #000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.document-card-header{
  background-color: #F7F9FB;
  border-bottom-width: 1px;
  position: sticky;
  top: 0px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
  gap: 8px;
  height: 34px;
  background: #F7F9FB;
  border-radius: 8px 8px 0px 0px;
  flex: none;
  order: 0;
  flex-grow: 0;    
  align-content: center;
  align-items: center;
  cursor:grab;
}

/* Loading spinner styles */
.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* New loading state styles */
.loading-state {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.loading-content {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.empty-content {
  text-align: center;
  padding: 2rem;
}

.skills-content {
  width: 100%;
  height: 100%;
}
</style>